'use client';
import { fromZonedTime } from 'date-fns-tz';
import toNumber from 'lodash/toNumber';
import React, { useEffect, useState } from 'react';

export interface CountdownClockProps {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  endsInText?: string;
}

interface TimeProps {
  days?: string;
  hours?: string;
  minutes?: string;
  seconds?: string;
}

export const getTZDate = (date: string) => fromZonedTime(new Date(date), 'America/Denver');

const CountDownClock = ({
  startDate,
  startTime,
  endDate,
  endTime,
  endsInText = 'Ends in',
}: CountdownClockProps) => {
  const promotionStartDate = React.useMemo(
    () => getTZDate(`${startDate} ${startTime}`),
    [startDate, startTime],
  );
  const promotionEndDate = React.useMemo(
    () => getTZDate(`${endDate} ${endTime}`),
    [endDate, endTime],
  );
  const isWithinPromotionPeriod = React.useCallback(
    (date: Date) => date > promotionStartDate && date < promotionEndDate,
    [promotionEndDate, promotionStartDate],
  );
  const calculateTimeLeft = React.useCallback((): TimeProps | null => {
    const now = new Date();
    if (!isWithinPromotionPeriod(now)) {
      return null;
    }

    const difference = Math.max(+promotionEndDate - +now, 0);
    const days = Math.floor(difference / (1000 * 60 * 60 * 24)).toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24).toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    const minutes = Math.floor((difference / 1000 / 60) % 60).toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    const seconds = Math.floor((difference / 1000) % 60).toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });

    return {
      days,
      hours,
      minutes,
      seconds,
    };
  }, [isWithinPromotionPeriod, promotionEndDate]);

  const [timeLeft, setTimeLeft] = useState<TimeProps | null>({});

  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      const timeLeft = calculateTimeLeft();
      if (!timeLeft) {
        clearInterval(timer);
      }
      setTimeLeft(timeLeft);
    }, 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  if (!timeLeft) {
    return null;
  }

  return (
    <p
      className="hidden md:inline-flex [font:inherit] gap-x-1"
      role="timer"
      aria-live="off"
      suppressHydrationWarning
      data-testid="countdown-clock"
    >
      <span>|</span>
      <span>
        {endsInText}&nbsp;
        {Object.entries(timeLeft).map(([interval, time]) =>
          toNumber(timeLeft[interval as keyof TimeProps]) === 0 && interval !== 'seconds' ? null : (
            <React.Fragment key={interval}>
              {time}
              {['days', 'hours', 'minutes'].includes(interval) && <>:</>}
            </React.Fragment>
          ),
        )}
      </span>
    </p>
  );
};

export default CountDownClock;
